import { useState, useCallback } from "react";
import {
  View,
  Text,
  TouchableOpacity,
  Modal,
  TextInput,
  ScrollView,
  Alert,
  ActivityIndicator,
} from "react-native";
import { Ionicons } from "@expo/vector-icons";
import { useTheme, useThemeColors } from "@/providers/ThemeProvider";
import { useAuth } from "@/providers/AuthProvider";
import { useReveal, RevealHint, RevealChallenge } from "@/modules/agenda/hooks/useReveal";
import { format, addHours } from "date-fns";
import { nl } from "date-fns/locale";
import DateTimePicker from "@react-native-community/datetimepicker";
import { Platform } from "react-native";

/**
 * Challenge type options
 */
const CHALLENGE_TYPES: { value: RevealChallenge["challenge_type"]; label: string; icon: string }[] = [
  { value: "quiz", label: "Quiz vraag", icon: "help-circle" },
  { value: "photo", label: "Foto opdracht", icon: "camera" },
  { value: "location", label: "Locatie check-in", icon: "location" },
  { value: "task", label: "Opdracht", icon: "checkbox" },
  { value: "puzzle", label: "Puzzel", icon: "extension-puzzle" },
];

interface RevealManagementProps {
  visible: boolean;
  onClose: () => void;
  eventId: string;
  eventTitle: string;
}

/**
 * Admin component for managing reveal hints and challenges
 */
export const RevealManagement = ({
  visible,
  onClose,
  eventId,
  eventTitle,
}: RevealManagementProps) => {
  const { isDark } = useTheme();
  const colors = useThemeColors();
  const { profile } = useAuth();
  const {
    hints,
    challenges,
    isLoading,
    addHint,
    addChallenge,
    deleteHint,
    deleteChallenge,
    releaseHint,
    triggerReveal,
    refetch,
  } = useReveal({ eventId, enabled: visible });

  const [activeTab, setActiveTab] = useState<"hints" | "challenges">("hints");
  const [showAddHint, setShowAddHint] = useState(false);
  const [showAddChallenge, setShowAddChallenge] = useState(false);

  // Add hint form state
  const [hintText, setHintText] = useState("");
  const [hintReleaseAt, setHintReleaseAt] = useState(addHours(new Date(), 1));
  const [showHintDatePicker, setShowHintDatePicker] = useState(false);
  const [isAddingHint, setIsAddingHint] = useState(false);

  // Add challenge form state
  const [challengeTitle, setChallengeTitle] = useState("");
  const [challengeDescription, setChallengeDescription] = useState("");
  const [challengeType, setChallengeType] = useState<RevealChallenge["challenge_type"]>("quiz");
  const [challengeReward, setChallengeReward] = useState("");
  const [isAddingChallenge, setIsAddingChallenge] = useState(false);

  /**
   * Handle adding a hint
   */
  const handleAddHint = useCallback(async () => {
    if (!hintText.trim()) {
      Alert.alert("Fout", "Vul een hint tekst in");
      return;
    }

    setIsAddingHint(true);
    try {
      const nextHintNumber = hints.length + 1;
      const success = await addHint({
        event_id: eventId,
        hint_number: nextHintNumber,
        hint_text: hintText.trim(),
        release_at: hintReleaseAt.toISOString(),
      });

      if (success) {
        setHintText("");
        setHintReleaseAt(addHours(new Date(), 1));
        setShowAddHint(false);
        Alert.alert("Succes", "Hint toegevoegd!");
      } else {
        Alert.alert("Fout", "Kon hint niet toevoegen");
      }
    } finally {
      setIsAddingHint(false);
    }
  }, [hintText, hintReleaseAt, hints.length, eventId, addHint]);

  /**
   * Handle adding a challenge
   */
  const handleAddChallenge = useCallback(async () => {
    if (!challengeTitle.trim()) {
      Alert.alert("Fout", "Vul een titel in");
      return;
    }

    setIsAddingChallenge(true);
    try {
      const nextOrder = challenges.length;
      const success = await addChallenge({
        event_id: eventId,
        title: challengeTitle.trim(),
        description: challengeDescription.trim() || undefined,
        challenge_type: challengeType,
        reward_text: challengeReward.trim() || undefined,
        order_index: nextOrder,
        created_by: profile?.id,
      });

      if (success) {
        setChallengeTitle("");
        setChallengeDescription("");
        setChallengeReward("");
        setShowAddChallenge(false);
        Alert.alert("Succes", "Uitdaging toegevoegd!");
      } else {
        Alert.alert("Fout", "Kon uitdaging niet toevoegen");
      }
    } finally {
      setIsAddingChallenge(false);
    }
  }, [challengeTitle, challengeDescription, challengeType, challengeReward, challenges.length, eventId, profile?.id, addChallenge]);

  /**
   * Handle deleting a hint
   */
  const handleDeleteHint = useCallback(
    (hint: RevealHint) => {
      Alert.alert(
        "Hint verwijderen",
        `Weet je zeker dat je hint ${hint.hint_number} wilt verwijderen?`,
        [
          { text: "Annuleer", style: "cancel" },
          {
            text: "Verwijderen",
            style: "destructive",
            onPress: async () => {
              const success = await deleteHint(hint.id);
              if (!success) {
                Alert.alert("Fout", "Kon hint niet verwijderen");
              }
            },
          },
        ]
      );
    },
    [deleteHint]
  );

  /**
   * Handle deleting a challenge
   */
  const handleDeleteChallenge = useCallback(
    (challenge: RevealChallenge) => {
      Alert.alert(
        "Uitdaging verwijderen",
        `Weet je zeker dat je "${challenge.title}" wilt verwijderen?`,
        [
          { text: "Annuleer", style: "cancel" },
          {
            text: "Verwijderen",
            style: "destructive",
            onPress: async () => {
              const success = await deleteChallenge(challenge.id);
              if (!success) {
                Alert.alert("Fout", "Kon uitdaging niet verwijderen");
              }
            },
          },
        ]
      );
    },
    [deleteChallenge]
  );

  /**
   * Handle releasing a hint
   */
  const handleReleaseHint = useCallback(
    async (hint: RevealHint) => {
      const success = await releaseHint(hint.id);
      if (success) {
        Alert.alert("Succes", `Hint ${hint.hint_number} is nu zichtbaar!`);
      } else {
        Alert.alert("Fout", "Kon hint niet vrijgeven");
      }
    },
    [releaseHint]
  );

  /**
   * Handle triggering the reveal
   */
  const handleTriggerReveal = useCallback(() => {
    Alert.alert(
      "Nu onthullen?",
      "Weet je zeker dat je dit evenement nu wilt onthullen? Dit kan niet ongedaan worden gemaakt.",
      [
        { text: "Annuleer", style: "cancel" },
        {
          text: "Onthullen",
          onPress: async () => {
            const success = await triggerReveal(eventId);
            if (success) {
              Alert.alert("Succes", "Evenement is onthuld! 🎉");
              onClose();
            } else {
              Alert.alert("Fout", "Kon evenement niet onthullen");
            }
          },
        },
      ]
    );
  }, [eventId, triggerReveal, onClose]);

  return (
    <Modal visible={visible} animationType="slide" onRequestClose={onClose}>
      <View className="flex-1" style={{ backgroundColor: colors.background }}>
        {/* Header */}
        <View
          className="flex-row items-center justify-between px-4 py-4 border-b"
          style={{ borderColor: colors.border, paddingTop: 50 }}
        >
          <TouchableOpacity onPress={onClose}>
            <Ionicons name="close" size={24} color={colors.text} />
          </TouchableOpacity>
          <Text className="text-lg font-bold flex-1 text-center mx-4" numberOfLines={1} style={{ color: colors.text }}>
            Reveal beheren
          </Text>
          <TouchableOpacity onPress={handleTriggerReveal}>
            <Text className="font-semibold" style={{ color: colors.warning }}>
              Nu onthullen
            </Text>
          </TouchableOpacity>
        </View>

        {/* Event title */}
        <View className="px-4 py-3" style={{ backgroundColor: colors.surfaceSecondary }}>
          <Text className="text-sm" style={{ color: colors.textSecondary }}>
            Evenement
          </Text>
          <Text className="font-medium" style={{ color: colors.text }}>
            {eventTitle}
          </Text>
        </View>

        {/* Tabs */}
        <View className="flex-row px-4 py-2 border-b" style={{ borderColor: colors.border }}>
          <TouchableOpacity
            onPress={() => setActiveTab("hints")}
            className="flex-1 py-2 items-center rounded-lg"
            style={{ backgroundColor: activeTab === "hints" ? colors.primaryLight : "transparent" }}
          >
            <Text
              className="font-medium"
              style={{ color: activeTab === "hints" ? colors.primary : colors.textSecondary }}
            >
              Hints ({hints.length})
            </Text>
          </TouchableOpacity>
          <TouchableOpacity
            onPress={() => setActiveTab("challenges")}
            className="flex-1 py-2 items-center rounded-lg"
            style={{ backgroundColor: activeTab === "challenges" ? colors.primaryLight : "transparent" }}
          >
            <Text
              className="font-medium"
              style={{ color: activeTab === "challenges" ? colors.primary : colors.textSecondary }}
            >
              Uitdagingen ({challenges.length})
            </Text>
          </TouchableOpacity>
        </View>

        {/* Content */}
        {isLoading ? (
          <View className="flex-1 items-center justify-center">
            <ActivityIndicator size="large" color={colors.primary} />
          </View>
        ) : (
          <ScrollView className="flex-1 px-4 py-4">
            {activeTab === "hints" ? (
              <>
                {/* Hints list */}
                {hints.map((hint) => (
                  <View
                    key={hint.id}
                    className="rounded-xl p-4 mb-3"
                    style={{ backgroundColor: colors.surface, borderWidth: 1, borderColor: colors.border }}
                  >
                    <View className="flex-row items-center justify-between">
                      <View className="flex-row items-center">
                        <View
                          className="w-8 h-8 rounded-full items-center justify-center"
                          style={{ backgroundColor: hint.is_released ? colors.successLight : colors.primaryLight }}
                        >
                          <Text className="font-bold" style={{ color: hint.is_released ? colors.success : colors.primary }}>
                            {hint.hint_number}
                          </Text>
                        </View>
                        <View className="ml-3 flex-1">
                          <Text numberOfLines={2} style={{ color: colors.text }}>
                            {hint.hint_text}
                          </Text>
                          <Text className="text-xs mt-1" style={{ color: colors.textTertiary }}>
                            Vrijgeven: {format(new Date(hint.release_at), "d MMM HH:mm", { locale: nl })}
                          </Text>
                        </View>
                      </View>
                      <View className="flex-row items-center">
                        {!hint.is_released && (
                          <TouchableOpacity
                            onPress={() => handleReleaseHint(hint)}
                            className="p-2 mr-1"
                          >
                            <Ionicons name="eye" size={20} color={colors.primary} />
                          </TouchableOpacity>
                        )}
                        <TouchableOpacity onPress={() => handleDeleteHint(hint)} className="p-2">
                          <Ionicons name="trash" size={20} color={colors.error} />
                        </TouchableOpacity>
                      </View>
                    </View>
                  </View>
                ))}

                {/* Add hint button */}
                <TouchableOpacity
                  onPress={() => setShowAddHint(true)}
                  className="flex-row items-center justify-center py-4 rounded-xl border-2 border-dashed"
                  style={{ borderColor: colors.border }}
                >
                  <Ionicons name="add-circle" size={24} color={colors.primary} />
                  <Text className="ml-2 font-medium" style={{ color: colors.primary }}>
                    Hint toevoegen
                  </Text>
                </TouchableOpacity>
              </>
            ) : (
              <>
                {/* Challenges list */}
                {challenges.map((challenge) => (
                  <View
                    key={challenge.id}
                    className="rounded-xl p-4 mb-3"
                    style={{ backgroundColor: colors.surface, borderWidth: 1, borderColor: colors.border }}
                  >
                    <View className="flex-row items-center justify-between">
                      <View className="flex-row items-center flex-1">
                        <View
                          className="w-10 h-10 rounded-full items-center justify-center"
                          style={{ backgroundColor: challenge.is_completed ? colors.successLight : colors.primaryLight }}
                        >
                          <Ionicons
                            name={CHALLENGE_TYPES.find((t) => t.value === challenge.challenge_type)?.icon as any || "help-circle"}
                            size={20}
                            color={challenge.is_completed ? colors.success : colors.primary}
                          />
                        </View>
                        <View className="ml-3 flex-1">
                          <Text className="font-medium" style={{ color: colors.text }}>
                            {challenge.title}
                          </Text>
                          <Text className="text-xs" style={{ color: colors.textTertiary }}>
                            {CHALLENGE_TYPES.find((t) => t.value === challenge.challenge_type)?.label}
                            {challenge.is_completed && " • Voltooid ✓"}
                          </Text>
                        </View>
                      </View>
                      <TouchableOpacity onPress={() => handleDeleteChallenge(challenge)} className="p-2">
                        <Ionicons name="trash" size={20} color={colors.error} />
                      </TouchableOpacity>
                    </View>
                  </View>
                ))}

                {/* Add challenge button */}
                <TouchableOpacity
                  onPress={() => setShowAddChallenge(true)}
                  className="flex-row items-center justify-center py-4 rounded-xl border-2 border-dashed"
                  style={{ borderColor: colors.border }}
                >
                  <Ionicons name="add-circle" size={24} color={colors.primary} />
                  <Text className="ml-2 font-medium" style={{ color: colors.primary }}>
                    Uitdaging toevoegen
                  </Text>
                </TouchableOpacity>
              </>
            )}
          </ScrollView>
        )}

        {/* Add Hint Modal */}
        <Modal visible={showAddHint} animationType="fade" transparent onRequestClose={() => setShowAddHint(false)}>
          <View className="flex-1 justify-end" style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
            <View className="rounded-t-3xl p-6" style={{ backgroundColor: colors.surface }}>
              <View className="flex-row items-center justify-between mb-4">
                <Text className="text-xl font-bold" style={{ color: colors.text }}>
                  Nieuwe Hint
                </Text>
                <TouchableOpacity onPress={() => setShowAddHint(false)}>
                  <Ionicons name="close" size={24} color={colors.textSecondary} />
                </TouchableOpacity>
              </View>

              <Text className="text-sm font-medium mb-2" style={{ color: colors.textSecondary }}>
                Hint tekst
              </Text>
              <TextInput
                value={hintText}
                onChangeText={setHintText}
                placeholder="Typ de hint..."
                placeholderTextColor={colors.textTertiary}
                multiline
                className="px-4 py-3 rounded-xl mb-4"
                style={{
                  backgroundColor: colors.surfaceSecondary,
                  color: colors.text,
                  minHeight: 80,
                  textAlignVertical: "top",
                }}
              />

              <Text className="text-sm font-medium mb-2" style={{ color: colors.textSecondary }}>
                Vrijgeven op
              </Text>
              <TouchableOpacity
                onPress={() => setShowHintDatePicker(true)}
                className="px-4 py-3 rounded-xl mb-4"
                style={{ backgroundColor: colors.surfaceSecondary }}
              >
                <Text style={{ color: colors.text }}>
                  {format(hintReleaseAt, "EEEE d MMMM yyyy HH:mm", { locale: nl })}
                </Text>
              </TouchableOpacity>

              <TouchableOpacity
                onPress={handleAddHint}
                disabled={isAddingHint || !hintText.trim()}
                className="py-4 rounded-xl items-center"
                style={{
                  backgroundColor: isAddingHint || !hintText.trim() ? colors.border : colors.primary,
                }}
              >
                {isAddingHint ? (
                  <ActivityIndicator color="#FFFFFF" />
                ) : (
                  <Text className="font-semibold text-white">Toevoegen</Text>
                )}
              </TouchableOpacity>

              {Platform.OS === "ios" && showHintDatePicker && (
                <DateTimePicker
                  value={hintReleaseAt}
                  mode="datetime"
                  display="spinner"
                  onChange={(e, date) => {
                    if (date) setHintReleaseAt(date);
                    setShowHintDatePicker(false);
                  }}
                  locale="nl-NL"
                  is24Hour
                />
              )}
            </View>
          </View>
        </Modal>

        {/* Add Challenge Modal */}
        <Modal visible={showAddChallenge} animationType="fade" transparent onRequestClose={() => setShowAddChallenge(false)}>
          <View className="flex-1 justify-end" style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
            <ScrollView>
              <View className="rounded-t-3xl p-6" style={{ backgroundColor: colors.surface, marginTop: 100 }}>
                <View className="flex-row items-center justify-between mb-4">
                  <Text className="text-xl font-bold" style={{ color: colors.text }}>
                    Nieuwe Uitdaging
                  </Text>
                  <TouchableOpacity onPress={() => setShowAddChallenge(false)}>
                    <Ionicons name="close" size={24} color={colors.textSecondary} />
                  </TouchableOpacity>
                </View>

                <Text className="text-sm font-medium mb-2" style={{ color: colors.textSecondary }}>
                  Titel
                </Text>
                <TextInput
                  value={challengeTitle}
                  onChangeText={setChallengeTitle}
                  placeholder="Titel van de uitdaging"
                  placeholderTextColor={colors.textTertiary}
                  className="px-4 py-3 rounded-xl mb-4"
                  style={{ backgroundColor: colors.surfaceSecondary, color: colors.text }}
                />

                <Text className="text-sm font-medium mb-2" style={{ color: colors.textSecondary }}>
                  Type
                </Text>
                <View className="flex-row flex-wrap mb-4">
                  {CHALLENGE_TYPES.map((type) => (
                    <TouchableOpacity
                      key={type.value}
                      onPress={() => setChallengeType(type.value)}
                      className="px-3 py-2 rounded-full mr-2 mb-2 flex-row items-center"
                      style={{
                        backgroundColor: challengeType === type.value ? colors.primary : colors.surfaceSecondary,
                      }}
                    >
                      <Ionicons
                        name={type.icon as any}
                        size={16}
                        color={challengeType === type.value ? "#FFFFFF" : colors.textSecondary}
                      />
                      <Text
                        className="ml-1 text-sm"
                        style={{ color: challengeType === type.value ? "#FFFFFF" : colors.text }}
                      >
                        {type.label}
                      </Text>
                    </TouchableOpacity>
                  ))}
                </View>

                <Text className="text-sm font-medium mb-2" style={{ color: colors.textSecondary }}>
                  Beschrijving (optioneel)
                </Text>
                <TextInput
                  value={challengeDescription}
                  onChangeText={setChallengeDescription}
                  placeholder="Beschrijf de uitdaging..."
                  placeholderTextColor={colors.textTertiary}
                  multiline
                  className="px-4 py-3 rounded-xl mb-4"
                  style={{
                    backgroundColor: colors.surfaceSecondary,
                    color: colors.text,
                    minHeight: 80,
                    textAlignVertical: "top",
                  }}
                />

                <Text className="text-sm font-medium mb-2" style={{ color: colors.textSecondary }}>
                  Beloning (optioneel)
                </Text>
                <TextInput
                  value={challengeReward}
                  onChangeText={setChallengeReward}
                  placeholder="Bijv. '10 punten' of 'Een verrassing'"
                  placeholderTextColor={colors.textTertiary}
                  className="px-4 py-3 rounded-xl mb-4"
                  style={{ backgroundColor: colors.surfaceSecondary, color: colors.text }}
                />

                <TouchableOpacity
                  onPress={handleAddChallenge}
                  disabled={isAddingChallenge || !challengeTitle.trim()}
                  className="py-4 rounded-xl items-center"
                  style={{
                    backgroundColor: isAddingChallenge || !challengeTitle.trim() ? colors.border : colors.primary,
                  }}
                >
                  {isAddingChallenge ? (
                    <ActivityIndicator color="#FFFFFF" />
                  ) : (
                    <Text className="font-semibold text-white">Toevoegen</Text>
                  )}
                </TouchableOpacity>
              </View>
            </ScrollView>
          </View>
        </Modal>

        {Platform.OS === "android" && showHintDatePicker && (
          <DateTimePicker
            value={hintReleaseAt}
            mode="datetime"
            display="default"
            onChange={(e, date) => {
              setShowHintDatePicker(false);
              if (date) setHintReleaseAt(date);
            }}
            is24Hour
          />
        )}
      </View>
    </Modal>
  );
};

export default RevealManagement;

